import { useState } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode';
import { useIsMobile } from '@/hooks/use-mobile';
import { getOrCreateConversation } from '@/lib/conversations';
import { useChatWidget } from '@/hooks/useChatWidget';

export const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { mode } = useWorkspaceMode();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { openChat } = useChatWidget();

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Close popover first to avoid blocking navigation
    setOpen(false);
    
    setTimeout(() => {
      // An explicit link always wins (used by broadcast/announcement notifications)
      if (typeof notification.link === 'string' && notification.link.startsWith('/')) {
        navigate(notification.link);
        return;
      }
      if (notification.type === 'welcome') {
        supabase.auth.getUser().then(async ({ data: { user } }) => {
          if (!user) return;
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .maybeSingle();
          if (profile?.role === 'other') {
            navigate('/admin', { state: { showForm: true } });
          } else {
            navigate(`/user/${user.id}`);
          }
        });
      } else if (notification.type === 'welcome_connect') {
        navigate('/community?tab=members');
      } else if (notification.type === 'direct_message') {
        if (!notification.from_user_id) {
          navigate('/messages');
        } else {
          getOrCreateConversation(notification.from_user_id)
            .then((id) => {
              if (isMobile) {
                navigate(`/messages/${id}`);
              } else {
                // Desktop: open the floating chat popup instead of leaving the page
                openChat(id);
              }
            })
            .catch(() => navigate('/messages'));
        }
      } else if (notification.type === 'friend_request' || notification.type === 'friend_accept') {
        if (notification.from_user_id) {
          navigate(`/user/${notification.from_user_id}`);
        }
      } else if (notification.type === 'activity_submission' && notification.activity_id) {
        navigate('/admin', { state: { editActivityId: notification.activity_id } });
      } else if (notification.type === 'draft_reminder') {
        navigate('/admin');
      } else if ((notification.type === 'activity_approved' || notification.type === 'activity_rejected') && notification.activity_id) {
        navigate('/admin');
      } else if (notification.type === 'noticeboard_submission') {
        navigate('/admin', { state: { section: 'noticeboard-pending' } });
      } else if (notification.type === 'noticeboard_approved' || notification.type === 'noticeboard_rejected') {
        navigate('/noticeboard');

      } else if (notification.type === 'activity_report' && notification.activity_id) {
        navigate(`/activity/${notification.activity_id}`, { state: { reportMessage: notification.message } });
      } else if (notification.type === 'ownership_claim' && notification.activity_id) {
        navigate(`/activity/${notification.activity_id}`, { state: { claimMessage: notification.message } });
      } else if (notification.type === 'new_activity' && notification.activity_id) {
        navigate(`/activity/${notification.activity_id}`);
      } else if (notification.type === 'play_date_invite' && notification.play_date_id) {
        navigate(`/play-date/${notification.play_date_id}`);
      } else if (notification.type === 'play_date_chat' && notification.play_date_id) {
        navigate(`/play-date/${notification.play_date_id}`);
      } else if (notification.type === 'play_date_comment' && notification.play_date_id) {
        navigate(`/play-date/${notification.play_date_id}`);
      } else if (notification.play_date_id && notification.type !== 'play_date_join' && notification.type !== 'play_date_leave') {
        // Any other meet-up alert (new meet-up nearby, tagged in a meet-up, …) opens the meet-up.
        navigate(`/play-date/${notification.play_date_id}`);
      } else if (notification.type === 'circle_invite') {
        const link = (notification as { link?: string | null }).link;
        navigate(link || '/community?tab=circles');
      } else if (notification.type === 'circle_chat') {
        const link = (notification as { link?: string | null }).link;
        navigate(link || '/community?tab=circles');
      } else if ((notification.type === 'play_date_join' || notification.type === 'play_date_leave') && notification.from_user_id) {
        navigate(`/user/${notification.from_user_id}`);
      } else if (notification.type === 'mutual_interest_match' && notification.from_user_id) {
        navigate(`/user/${notification.from_user_id}`);
      } else if (notification.type === 'open_to_meet_going' && notification.activity_id && notification.from_user_id) {
        const todayStr = new Date().toISOString().split('T')[0];
        supabase
          .from('activity_participants')
          .select('planned_date')
          .eq('activity_id', notification.activity_id)
          .eq('user_id', notification.from_user_id)
          .gte('planned_date', todayStr)
          .order('planned_date', { ascending: true })
          .limit(1)
          .maybeSingle()
          .then(({ data }) => {
            const date = data?.planned_date;
            navigate(date ? `/activity/${notification.activity_id}?date=${date}` : `/activity/${notification.activity_id}`);
          });
      } else if (notification.type === 'going_reminder' && notification.activity_id) {
        navigate(`/activity/${notification.activity_id}?reminder=1`);
      } else if (notification.type === 'listing_going' && notification.activity_id && notification.from_user_id) {
        const todayStr = new Date().toISOString().split('T')[0];
        supabase
          .from('activity_participants')
          .select('planned_date')
          .eq('activity_id', notification.activity_id)
          .eq('user_id', notification.from_user_id)
          .gte('planned_date', todayStr)
          .order('planned_date', { ascending: true })
          .limit(1)
          .maybeSingle()
          .then(({ data }) => {
            const date = data?.planned_date;
            navigate(date ? `/activity/${notification.activity_id}?date=${date}` : `/activity/${notification.activity_id}`);
          });
      } else if (notification.type === 'listing_co_going') {
        navigate(notification.link || `/activity/${notification.activity_id}`);
      } else if ((notification.type === 'listing_interest' || notification.type === 'listing_going_term') && notification.activity_id) {
        navigate(`/activity/${notification.activity_id}`);
      } else if (notification.activity_id) {
        navigate(`/activity/${notification.activity_id}`);
      }
    }, 150);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Notifications" className="relative h-9 w-9 p-2 [&_svg]:size-5">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center p-0 px-1 bg-accent text-accent-foreground text-[9px] border-0"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-8 text-xs"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-light-fuschia/10' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {notification.title}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="h-2 w-2 rounded-full bg-accent" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 text-xs text-muted-foreground"
            onClick={() => {
              setOpen(false);
              setTimeout(() => navigate(mode === 'business' ? '/business?tab=settings' : '/settings'), 150);
            }}
          >
            Manage notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
